import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle2, Stethoscope } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegistrationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState(null);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const checkStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      setError('Account creation is taking longer than expected. Please try logging in with your credentials.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/registration-status/${sessionId}`);
      
      if (response.data.status === 'success') {
        // Auto-login the user
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Update auth context
        authLogin(response.data.access_token, response.data.user);
        
        setStatus('success');
        
        // Redirect to dashboard after a moment
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else if (response.data.status === 'processing' || response.data.status === 'pending') {
        // Still processing, poll again
        setTimeout(() => checkStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError(response.data.message || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error checking registration status:', err);
      // Retry on network errors
      setTimeout(() => checkStatus(attempts + 1), pollInterval);
    }
  }, [sessionId, authLogin, navigate]);

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    } else {
      setStatus('error');
      setError('No session ID provided');
    }
  }, [sessionId, checkStatus]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#00A651] flex items-center justify-center">
              <Stethoscope className="w-9 h-9 text-white" />
            </div>
          </div>

          {status === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#00A651] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Setting up your account...</h1>
              <p className="text-slate-600">Please wait while we activate your subscription.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome to Trac Jobs Alert!</h1>
              <p className="text-slate-600 mb-4">Your account has been created and your subscription is active.</p>
              <p className="text-sm text-slate-500">Redirecting to your dashboard...</p>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Almost there!</h1>
              <p className="text-slate-600 mb-4">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-[#00A651] text-white rounded-full hover:bg-[#008C44] transition-colors"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccessPage;
